import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { format, differenceInDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Search, Calendar, User, Building2, AlertTriangle, Clock, Eye, X, CheckCircle2, FileText, ZoomIn, Car, MapPin, Smartphone, Monitor, Loader2, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';


interface UrbanCampaign {
    id: string;
    advertiser_user_id: string;
    campaign_name: string;
    campaign_description: string | null;
    media_ids: string[];
    circulation_cities: string[];
    start_date: string;
    end_date: string;
    target_mobile: boolean;
    target_fixed: boolean;
    status: 'pending' | 'active' | 'paused' | 'ended' | 'rejected' | 'correction' | 'approved';
    admin_comment: string | null;
    total_impressions: number;
    total_clicks: number;
    total_audited_trips: number;
    created_at: string;
    updated_at: string;
    advertiser_profile?: {
        full_name: string;
        company_name: string;
        email?: string;
        avatar_url?: string;
    };
}

interface Media {
    id: string;
    title: string;
    url: string;
    type: string;
}

interface CampaignHistoryLog {
    id: string;
    new_status: string;
    admin_comment: string;
    created_at: string;
}

// Lista de cidades disponíveis (mesma do WatchAdsModal)
const AVAILABLE_CITIES: { value: string; label: string }[] = [
    { value: 'florianopolis', label: 'Florianópolis' },
    { value: 'sao-jose', label: 'São José' },
    { value: 'palhoca', label: 'Palhoça' },
    { value: 'biguacu', label: 'Biguaçu' },
    { value: 'balneario-camboriu', label: 'Balneário Camboriú' },
    { value: 'itapema', label: 'Itapema' },
];

const getCityLabel = (cityValue: string) => {
    return AVAILABLE_CITIES.find(c => c.value === cityValue)?.label || cityValue;
};


const UrbanCampaignsSection: React.FC = () => {
    const [campaigns, setCampaigns] = useState<UrbanCampaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Review Modal State
    const [isReviewOpen, setIsReviewOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState<UrbanCampaign | null>(null);
    const [previewMedias, setPreviewMedias] = useState<Media[]>([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [reviewStatus, setReviewStatus] = useState<string>('pending');
    const [reviewComment, setReviewComment] = useState('');
    const [historyLogs, setHistoryLogs] = useState<CampaignHistoryLog[]>([]);
    const [zoomedMedia, setZoomedMedia] = useState<Media | null>(null);


    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            console.log('🚗 Buscando campanhas urban via RPC...');

            // Tentar usar a função RPC que contorna RLS
            let data: any[] | null = null;
            let error: any = null;

            // Primeiro, tentar via RPC (SECURITY DEFINER)
            const rpcResult = await supabase.rpc('admin_get_urban_campaigns');

            if (rpcResult.error) {
                console.warn('🚗 Função RPC não disponível, tentando query direta:', rpcResult.error.message);
                // Fallback para query direta
                const directResult = await supabase
                    .from('urban_campaigns')
                    .select('*')
                    .order('created_at', { ascending: false });

                data = directResult.data;
                error = directResult.error;
            } else {
                data = rpcResult.data;
                error = rpcResult.error;
            }

            console.log('🚗 Resultado da busca urban_campaigns:', { data, error, count: data?.length });

            if (error) {
                console.error('🚗 Erro na query urban_campaigns:', error);
                throw error;
            }

            // Buscar perfis dos anunciantes (manual join pois a relação pode não estar definida no client types)
            if (data) {
                const advertiserIds = [...new Set(data.map(c => c.advertiser_user_id).filter(Boolean))];

                if (advertiserIds.length > 0) {
                    const { data: profiles, error: profilesError } = await supabase
                        .from('profiles')
                        .select('id, full_name, company_name, avatar_url')
                        .in('id', advertiserIds);

                    if (!profilesError && profiles) {
                        // Map profiles to dictionary
                        const profileMap = profiles.reduce((acc, profile) => {
                            acc[profile.id] = profile;
                            return acc;
                        }, {} as Record<string, any>);

                        // Attach to campaigns
                        const enrichedData = data.map(campaign => ({
                            ...campaign,
                            advertiser_profile: profileMap[campaign.advertiser_user_id]
                        }));

                        setCampaigns(enrichedData);
                    } else {
                        setCampaigns(data);
                    }
                } else {
                    setCampaigns(data);
                }
            }
        } catch (error) {
            console.error('Erro ao buscar campanhas Urban:', error);
            setMessage({ type: 'error', text: 'Erro ao carregar campanhas Urban.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const fetchPreviewMedias = async (mediaIds: string[]) => {
        if (!mediaIds || mediaIds.length === 0) {
            setPreviewMedias([]);
            return;
        }

        setLoadingPreview(true);
        try {
            const { data, error } = await supabase
                .from('medias')
                .select('*')
                .in('id', mediaIds);

            if (error) throw error;
            setPreviewMedias(data || []);
        } catch (err) {
            console.error('Erro ao buscar mídias:', err);
        } finally {
            setLoadingPreview(false);
        }
    };

    const fetchCampaignHistory = async (campaignId: string) => {
        try {
            console.log('📋 Buscando histórico para campanha:', campaignId);

            const { data, error } = await supabase
                .from('urban_campaign_history')
                .select('*')
                .eq('campaign_id', campaignId)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('❌ Erro ao buscar histórico:', error);
                setHistoryLogs([]);
                return;
            }

            console.log('📋 Histórico encontrado:', data?.length || 0, 'registros');

            if (data) {
                setHistoryLogs(data);
            } else {
                setHistoryLogs([]);
            }
        } catch (err) {
            console.error('❌ Erro inesperado ao buscar histórico:', err);
            setHistoryLogs([]);
        }
    };

    const handleOpenReview = (campaign: UrbanCampaign) => {
        setSelectedCampaign(campaign);
        setReviewStatus(campaign.status);
        setReviewComment(''); // Reset input to empty, clean state for new comments
        setIsReviewOpen(true);
        if (campaign.media_ids && campaign.media_ids.length > 0) {
            fetchPreviewMedias(campaign.media_ids);
        } else {
            setPreviewMedias([]);
        }
        fetchCampaignHistory(campaign.id);
    };

    const handleSaveReview = async () => {
        if (!selectedCampaign) return;

        setUpdatingId(selectedCampaign.id);
        try {
            // Atualizar a campanha
            const { error } = await supabase
                .from('urban_campaigns')
                .update({
                    status: reviewStatus,
                    admin_comment: reviewComment?.trim() || null
                })
                .eq('id', selectedCampaign.id);

            if (error) throw error;

            // Update local state
            setCampaigns(prev => prev.map(c =>
                c.id === selectedCampaign.id
                    ? { ...c, status: reviewStatus as any, admin_comment: reviewComment }
                    : c
            ));

            // Insert history log - apenas se tiver comentário ou mudança de status
            const hasComment = reviewComment?.trim();
            const statusChanged = reviewStatus !== selectedCampaign.status;

            if (hasComment || statusChanged) {
                console.log('📝 Inserindo histórico:', {
                    campaign_id: selectedCampaign.id,
                    previous_status: selectedCampaign.status,
                    new_status: reviewStatus,
                    admin_comment: reviewComment?.trim() || null
                });

                const { data: historyData, error: historyError } = await supabase
                    .from('urban_campaign_history')
                    .insert({
                        campaign_id: selectedCampaign.id,
                        previous_status: selectedCampaign.status,
                        new_status: reviewStatus,
                        admin_comment: reviewComment?.trim() || null,
                        created_by: (await supabase.auth.getUser()).data.user?.id
                    })
                    .select();

                if (historyError) {
                    console.error("❌ Erro ao inserir histórico:", historyError);
                    // Mostrar alerta para o usuário
                    alert(`Atenção: Campanha atualizada, mas erro ao salvar histórico: ${historyError.message}`);
                } else {
                    console.log('✅ Histórico inserido com sucesso:', historyData);
                }
            }

            // Refresh history após inserção
            await fetchCampaignHistory(selectedCampaign.id);

            setMessage({ type: 'success', text: 'Campanha atualizada com sucesso!' });
            setReviewComment(''); // Reset input

        } catch (error) {
            console.error('Erro ao atualizar campanha:', error);
            setMessage({ type: 'error', text: 'Erro ao salvar alterações.' });
        } finally {
            setUpdatingId(null);
        }
    };

    // Filtrar campanhas
    const filteredCampaigns = campaigns.filter(campaign => {
        const query = searchQuery.toLowerCase();
        const advertiserName = campaign.advertiser_profile?.full_name?.toLowerCase() || '';
        const companyName = campaign.advertiser_profile?.company_name?.toLowerCase() || '';
        const campaignName = campaign.campaign_name?.toLowerCase() || '';
        const cities = campaign.circulation_cities?.join(' ').toLowerCase() || '';

        return advertiserName.includes(query) ||
            companyName.includes(query) ||
            campaignName.includes(query) ||
            cities.includes(query);
    });

    const getDurationDays = (start: string, end: string) => {
        try {
            const startDate = parseISO(start);
            const endDate = parseISO(end);
            return differenceInDays(endDate, startDate) + 1;
        } catch (e) {
            return 0;
        }
    };

    const getDayStatus = (start: string, end: string) => {
        try {
            const now = new Date();
            const startDate = parseISO(start);
            const endDate = parseISO(end);

            if (now < startDate) return 'Agendado';
            if (now > endDate) return 'Finalizado';
            return 'Em andamento';
        } catch (e) {
            return '-';
        }
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'approved':
                return 'bg-emerald-100 text-emerald-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'correction':
                return 'bg-orange-100 text-orange-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            case 'paused':
            case 'ended':
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'active': return 'Em veiculação';
            case 'approved': return 'Aprovada';
            case 'pending': return 'Pendente';
            case 'correction': return 'Correção';
            case 'rejected': return 'Rejeitada';
            case 'paused': return 'Pausada';
            case 'ended': return 'Finalizada';
            default: return status;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Car className="h-6 w-6 text-purple-600" />
                    Campanhas Urban (Veículos)
                </h2>
                <div className="flex items-center space-x-2 bg-white rounded-md border shadow-sm px-3 py-2 w-full max-w-sm">
                    <Search className="h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por anunciante, campanha ou cidade..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 outline-none text-sm"
                    />
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-md flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                    {message.type === 'error' && <AlertTriangle className="h-5 w-5" />}
                    {message.text}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Anunciante / Campanha
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cidades (Corridas auditadas)
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Período / Duração
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Targeting
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ação / Revisão
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCampaigns.length > 0 ? (
                                    filteredCampaigns.map((campaign) => (
                                        <tr key={campaign.id} className="hover:bg-purple-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                                        {campaign.advertiser_profile?.avatar_url ? (
                                                            <img
                                                                src={campaign.advertiser_profile.avatar_url}
                                                                alt={campaign.advertiser_profile.full_name || 'Anunciante'}
                                                                className="h-10 w-10 object-cover"
                                                            />
                                                        ) : (
                                                            <span className="text-white font-bold">
                                                                {campaign.advertiser_profile?.full_name?.charAt(0) || campaign.campaign_name.charAt(0) || 'A'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {campaign.advertiser_profile?.company_name || campaign.advertiser_profile?.full_name || 'Anunciante desconhecido'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {campaign.campaign_name}
                                                        </div>
                                                        <div className="text-xs text-gray-400">
                                                            {campaign.media_ids?.length || 0} mídia(s)
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                    {campaign.circulation_cities?.slice(0, 3).map(city => (
                                                        <Badge key={city} className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0 border-0">
                                                            {getCityLabel(city)}
                                                        </Badge>
                                                    ))}
                                                    {(campaign.circulation_cities?.length || 0) > 3 && (
                                                        <Badge className="bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0 border-0">
                                                            +{(campaign.circulation_cities?.length || 0) - 3}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col text-sm text-gray-900">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="h-3 w-3 text-gray-400" />
                                                        {format(parseISO(campaign.start_date), 'dd/MM/yyyy')} - {format(parseISO(campaign.end_date), 'dd/MM/yyyy')}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                                                        <Clock className="h-3 w-3" />
                                                        {getDurationDays(campaign.start_date, campaign.end_date)} dias ({getDayStatus(campaign.start_date, campaign.end_date)})
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {campaign.target_mobile && (
                                                        <div className="flex items-center gap-1 text-xs text-purple-600" title="Veículos móveis">
                                                            <Car className="h-4 w-4" />
                                                            <span>Móvel</span>
                                                        </div>
                                                    )}
                                                    {campaign.target_fixed && (
                                                        <div className="flex items-center gap-1 text-xs text-orange-600" title="Pontos fixos">
                                                            <Monitor className="h-4 w-4" />
                                                            <span>Fixo</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(campaign.status)}`}>
                                                    {getStatusLabel(campaign.status)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleOpenReview(campaign)}
                                                        className="text-gray-500 hover:text-purple-600 p-1 h-8 w-8 rounded-full"
                                                        title="Revisar Mídia e Status"
                                                    >
                                                        <Eye className="h-5 w-5" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex flex-col items-center justify-center">
                                                <Car className="h-10 w-10 text-gray-300 mb-2" />
                                                <p>Nenhuma campanha Urban encontrada.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
                <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col gap-0 overflow-hidden bg-white border-0 shadow-2xl">
                    <div className="px-6 py-4 border-b shrink-0 bg-white">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-xl">
                                <Car className="h-6 w-6 text-purple-600" />
                                Revisão de Campanha Urban
                            </DialogTitle>
                            <DialogDescription className="text-gray-500">
                                Analise a mídia, o histórico e defina o status da campanha.
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    {selectedCampaign && (
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 divide-x divide-gray-100 overflow-hidden">
                            {/* Left: Media Preview & History */}
                            <div className="h-full overflow-y-auto scrollbar-hide p-6 bg-gray-50/50 space-y-6">
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                                        <Eye className="h-4 w-4 text-purple-600" />
                                        Visualização da Mídia
                                    </h4>
                                    <div className="bg-white rounded-xl p-4 min-h-[300px] flex items-center justify-center border border-gray-200 shadow-sm">
                                        {loadingPreview ? (
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                        ) : previewMedias.length > 0 ? (
                                            <div className="space-y-4 w-full">
                                                {previewMedias.map((media) => (
                                                    <div key={media.id} className="relative group rounded-lg overflow-hidden bg-black shadow-sm ring-1 ring-black/5">
                                                        {media.type === 'video' || media.url.match(/\.(mp4|webm)$/i) ? (
                                                            <video
                                                                src={media.url}
                                                                controls
                                                                className="w-full h-auto max-h-[350px]"
                                                            />
                                                        ) : (
                                                            <img
                                                                src={media.url}
                                                                alt={media.title}
                                                                className="w-full h-auto max-h-[350px] object-contain"
                                                            />
                                                        )}

                                                        {/* Zoom Overlay */}
                                                        <div
                                                            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer z-10"
                                                            onClick={() => setZoomedMedia(media)}
                                                        >
                                                            <ZoomIn className="text-white h-10 w-10 drop-shadow-lg transform hover:scale-110 transition-transform" />
                                                        </div>

                                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white p-3 pt-6 text-xs z-20 pointer-events-none">
                                                            {media.title} ({media.type})
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-gray-400 text-sm flex flex-col items-center gap-2">
                                                <AlertTriangle className="h-8 w-8 opacity-20" />
                                                Nenhuma mídia encontrada
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cidades de Circulação */}
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                                        <MapPin className="h-4 w-4 text-purple-600" />
                                        Cidades de Circulação (Auditadas)
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedCampaign.circulation_cities?.map(city => (
                                            <Badge key={city} variant="secondary" className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-100">
                                                {getCityLabel(city)}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* History Section */}
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-purple-600" />
                                        Histórico de Alterações
                                    </h4>
                                    <div className="bg-white border border-gray-200 rounded-xl p-0 overflow-hidden shadow-sm">
                                        <div className="max-h-[250px] overflow-y-auto scrollbar-hide p-4 space-y-4">
                                            {historyLogs.length > 0 ? historyLogs.map((log, index) => (
                                                <div key={log.id} className={`text-xs ${index !== historyLogs.length - 1 ? 'border-b border-gray-100 pb-3' : ''}`}>
                                                    <div className="flex justify-between items-center mb-1.5">
                                                        <span className="text-gray-400 font-medium">{format(parseISO(log.created_at), "dd/MM 'às' HH:mm")}</span>
                                                        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 border h-5 ${log.new_status === 'active' ? 'text-green-600 border-green-200 bg-green-50' :
                                                            log.new_status === 'rejected' ? 'text-red-600 border-red-200 bg-red-50' :
                                                                log.new_status === 'correction' ? 'text-orange-600 border-orange-200 bg-orange-50' :
                                                                    'text-gray-600 bg-gray-50'
                                                            }`}>{getStatusLabel(log.new_status)}</Badge>
                                                    </div>
                                                    <p className="text-gray-600 leading-relaxed bg-gray-50 p-2 rounded-lg mt-1">
                                                        {log.admin_comment || <span className="text-gray-300 italic">Sem comentário adicional</span>}
                                                    </p>
                                                </div>
                                            )) : (
                                                <div className="text-center py-8">
                                                    <p className="text-gray-400 text-xs">Nenhum registro de histórico.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Details & Actions */}
                            <div className="h-full overflow-y-auto scrollbar-hide p-6 bg-white space-y-8">
                                <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-white p-5 rounded-2xl border border-purple-100 shadow-sm">
                                    <div className="mb-4">
                                        <h3 className="font-bold text-xl text-gray-900 leading-tight">{selectedCampaign.campaign_name}</h3>
                                        <p className="text-sm text-gray-500 mt-1 font-medium">{selectedCampaign.advertiser_profile?.company_name || selectedCampaign.advertiser_profile?.full_name}</p>
                                    </div>

                                    {selectedCampaign.campaign_description && (
                                        <div className="bg-white/60 p-3 rounded-lg border border-purple-100/50 mb-4 backdrop-blur-sm">
                                            <p className="text-sm text-gray-600 italic leading-relaxed">"{selectedCampaign.campaign_description}"</p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Início</span>
                                            <span className="font-medium text-gray-700">{format(parseISO(selectedCampaign.start_date), 'dd/MM/yyyy')}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Término</span>
                                            <span className="font-medium text-gray-700">{format(parseISO(selectedCampaign.end_date), 'dd/MM/yyyy')}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Mídias</span>
                                            <span className="font-medium text-gray-700 flex items-center gap-1">
                                                <FileText className="h-3.5 w-3.5 text-gray-400" />
                                                {selectedCampaign.media_ids?.length || 0} arquivo(s)
                                            </span>
                                        </div>
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Targeting</span>
                                            <div className="flex gap-1.5">
                                                {selectedCampaign.target_mobile && <Badge variant="secondary" className="bg-white text-purple-700 border border-purple-100 text-[10px] shadow-sm">Móvel</Badge>}
                                                {selectedCampaign.target_fixed && <Badge variant="secondary" className="bg-white text-orange-700 border border-orange-100 text-[10px] shadow-sm">Fixo</Badge>}
                                            </div>
                                        </div>
                                        <div className="col-span-2 pt-2 border-t border-purple-100 mt-2">
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Métricas (Impressões / Cliques / Corridas)</span>
                                            <span className="font-mono text-xs text-gray-600 bg-white px-2 py-1 rounded border border-purple-100 inline-block">
                                                {selectedCampaign.total_impressions?.toLocaleString() || 0} / {selectedCampaign.total_clicks?.toLocaleString() || 0} / {selectedCampaign.total_audited_trips?.toLocaleString() || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="space-y-3">
                                        <Label htmlFor="status" className="text-sm font-semibold text-gray-700">Status da Campanha</Label>
                                        <Select value={reviewStatus} onValueChange={setReviewStatus}>
                                            <SelectTrigger className="w-full h-11 bg-gray-50 border-gray-200 hover:border-purple-300 focus:ring-purple-200 transition-all">
                                                <SelectValue placeholder="Selecione o status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="pending">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.5)]" />
                                                        Pendente
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="active">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                                                        Em veiculação (Ativo)
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="correction">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.5)]" />
                                                        Solicitar Correção
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="rejected">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
                                                        Não Aprovada (Rejeitada)
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="paused">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-gray-500" />
                                                        Pausada
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="ended">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-gray-400" />
                                                        Finalizada
                                                    </div>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-3">
                                        <Label htmlFor="comment" className="text-sm font-semibold text-gray-700 flex justify-between">
                                            Comentário / Feedback
                                            <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wider">Visível para o anunciante</span>
                                        </Label>
                                        <Textarea
                                            id="comment"
                                            placeholder="Digite uma mensagem para o anunciante (ex: motivo da reprovação ou confirmação de ativação)..."
                                            value={reviewComment}
                                            onChange={(e) => setReviewComment(e.target.value)}
                                            className="min-h-[120px] bg-gray-50 border-gray-200 focus:border-purple-300 focus:ring-purple-200 resize-none p-3 text-sm"
                                        />
                                        <div className="flex items-start gap-2 bg-blue-50 p-2 rounded-lg text-blue-700 text-xs">
                                            <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                            <p>Este comentário será enviado ao cliente e ficará registrado no histórico da campanha.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="px-6 py-4 border-t bg-gray-50 shrink-0 flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setIsReviewOpen(false)} className="border-gray-300">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSaveReview}
                            disabled={updatingId !== null}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md hover:shadow-lg transition-all"
                        >
                            {updatingId ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Salvar
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Media Zoom Dialog */}
            <Dialog open={!!zoomedMedia} onOpenChange={() => setZoomedMedia(null)}>
                <DialogContent className="max-w-[90vw] max-h-[90vh] bg-black border-gray-800 p-0 overflow-hidden flex items-center justify-center outline-none">
                    <div className="relative w-full h-full flex items-center justify-center">
                        <Button
                            className="absolute top-4 right-4 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white border border-white/20"
                            size="icon"
                            onClick={() => setZoomedMedia(null)}
                        >
                            <X className="h-6 w-6" />
                        </Button>
                        {zoomedMedia && (
                            zoomedMedia.type === 'video' || zoomedMedia.url.match(/\.(mp4|webm)$/i) ? (
                                <video
                                    src={zoomedMedia.url}
                                    controls
                                    autoPlay
                                    className="max-w-full max-h-[90vh] w-auto h-auto outline-none"
                                />
                            ) : (
                                <img
                                    src={zoomedMedia.url}
                                    alt={zoomedMedia.title}
                                    className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
                                />
                            )
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UrbanCampaignsSection;
